// Mandelbrot, double-double (f64) precision — same z*z+c/|z|>2 shape as
// mandelbrot.wgsl, but with ComplexDD (~48-bit mantissa) instead of f32
// Complex, pushing zoom past the ~1e-5 depth where f32 pixelates.
//
// Every ComplexDD op hits a workgroupBarrier() (see dekker.wgsl's header),
// which requires all invocations in the workgroup to call it the same
// number of times — so no per-pixel `break`/early `return`, here or on the
// out-of-canvas edge threads. Every thread runs to max_iter unconditionally;
// an escaped pixel's z freezes via select() instead of skipping work.

@group(0) @binding(0) var<storage, read_write> output: array<u32>;

struct Params {
  width:        u32,
  height:       u32,
  center_re_hi: f32,
  center_re_lo: f32,
  center_im_hi: f32,
  center_im_lo: f32,
  scale_hi:     f32,
  scale_lo:     f32,
  max_iter:     u32,
}
@group(0) @binding(1) var<uniform> params: Params;

@compute @workgroup_size(8, 8)
fn main(
  @builtin(global_invocation_id) gid: vec3u,
  @builtin(local_invocation_index) lidx: u32,
) {
  let inBounds = gid.x < params.width && gid.y < params.height;

  let escapeR2 = DD(4.0, 0.0); // |z| > 2  <=>  |z|^2 > 4

  let centerRe = DD(params.center_re_hi, params.center_re_lo);
  let centerIm = DD(params.center_im_hi, params.center_im_lo);
  let scale    = DD(params.scale_hi, params.scale_lo);

  let c = pixelToComplexDD(gid.x, gid.y, params.width, params.height, centerRe, centerIm, scale, lidx);
  var z = ComplexDD(DD(0.0, 0.0), DD(0.0, 0.0));

  var iter: u32 = 0u;
  var escaped = false;
  var escapeIter: u32 = 0u;

  loop {
    if (iter >= params.max_iter) { break; }

    let zNext = cdAdd(cdMul(z, z, lidx), c, lidx);
    let live = !escaped; // still iterating (as opposed to frozen post-escape)

    // cdAbs2 must run unconditionally — `&&` short-circuits, and gating its barrier-calling code on non-uniform `live` would violate workgroupBarrier()'s uniform-control-flow requirement.
    let crossedEscapeRadius = ddGreater(cdAbs2(zNext, lidx), escapeR2);
    let nowEscaped = live && crossedEscapeRadius;

    z = ComplexDD(
      DD(select(z.re.hi, zNext.re.hi, live), select(z.re.lo, zNext.re.lo, live)),
      DD(select(z.im.hi, zNext.im.hi, live), select(z.im.lo, zNext.im.lo, live)),
    );

    if (nowEscaped) { escapeIter = iter; }
    escaped = escaped || nowEscaped;
    iter = iter + 1u;
  }

  if (inBounds) {
    let outIter = select(params.max_iter, escapeIter, escaped);
    output[gid.y * params.width + gid.x] = colorize(outIter, params.max_iter, Complex(z.re.hi, z.im.hi));
  }
}
